/**
 * Vellatini collection tab row — minimal sticky-state observer.
 *
 * The row is CSS `position: sticky` already (see sections/vellatini-collection-
 * tabs.liquid); this only toggles `.is-stuck` for a hairline/shadow hook once
 * it detaches from normal flow. Kept intentionally thin per phase-03 step 5
 * ("sticky behavior itself can be minimal now, Phase 9 polishes") — no active-
 * tab scroll-spy, no filter-drawer wiring yet. The stuck state itself is
 * unmocked (spec-17 ch.6 QA note); the shipped treatment is background =
 * page color, no box-shadow, hairline border only once stuck.
 *
 * Sticky against `.page-wrapper` (Horizon's own ≥990px scroll container —
 * `assets/base.css`: `.page-wrapper { overflow-y: auto; }` at that
 * breakpoint, while `<html>`/`<body>` get `overflow: hidden`, NOT `window`).
 * A default (viewport-rooted) IntersectionObserver would only coincidentally
 * match `.page-wrapper` scrolling since the wrapper happens to fill the
 * full viewport height — explicit `root` avoids relying on that
 * coincidence and stays correct if `.page-wrapper`'s box ever stops
 * matching the viewport exactly (e.g. the drawer-open margin-shift state).
 *
 * Wrapped in an idempotent init so the Shopify theme editor's section
 * re-render (`shopify:section:load`, fired after any section's HTML is
 * replaced in the editor) rebinds observers on new sentinel/row pairs
 * instead of leaving newly rendered rows without their sticky-state
 * observer (Phase 3 code-review note: "the module won't rebind after
 * section re-render").
 */
function initVellatiniStickyTabs(searchRoot = document) {
  searchRoot.querySelectorAll('.vt-tabs--sticky:not([data-sticky-bound])').forEach((row) => {
    row.setAttribute('data-sticky-bound', 'true');

    const sentinel = document.createElement('span');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'display:block;height:1px;';
    // The element that actually sticks is the .shopify-section wrapper (see the
    // liquid): a sentinel inside it rides along and never leaves the root, so
    // park it in flow just before the wrapper instead.
    (row.closest('.shopify-section') || row).before(sentinel);

    // Below 990px .page-wrapper isn't a scroll container (base.css scopes
    // `overflow-y: auto` to the ≥990px media query) - null root there
    // correctly falls back to the viewport, matching the mobile document
    // scroll.
    const scrollContainer = window.matchMedia('(min-width: 990px)').matches
      ? document.querySelector('.page-wrapper')
      : null;

    // The row now parks UNDER the sticky quiet-row header (top =
    // --header-height, sections/header.liquid 2026-09-04), so the sentinel is
    // hidden behind the header rather than scrolled out of the root: shrink
    // the root's top edge by the header height or is-stuck never fires.
    const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;

    const observer = new IntersectionObserver(
      ([entry]) => {
        row.classList.toggle('is-stuck', !entry.isIntersecting);
      },
      { root: scrollContainer, threshold: 0, rootMargin: `-${headerHeight}px 0px 0px 0px` }
    );

    observer.observe(sentinel);
  });
}

initVellatiniStickyTabs();

document.addEventListener('shopify:section:load', (event) => {
  initVellatiniStickyTabs(/** @type {Element} */ (event.target) ?? document);
});
